import OLCollection from 'ol/Collection'
import OLLayerGroup from 'ol/layer/Group'

import type OLLayer from 'ol/layer/Layer'

import type { ConfigLayerCapabilityOptions } from 'config/catalog/ConfigLayerCapability'
import { ConfigLayerCapability } from 'config/catalog/ConfigLayerCapability'
import { createOlLayerFromLayerConf } from 'config/config'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { ConfigLayerType } from 'config/ConfigLayerType'
import { MapLayer } from 'config/MapLayer'
import { setLayerSelected, setLayerUnselected } from 'drawer/tabs/layers/actions'

export class MapLayerCapabilities extends MapLayer {
  static async createLayer (capabilityOptions: ConfigLayerCapabilityOptions) {
    this.preLayer(capabilityOptions)

    try {
      const capability = await ConfigLayerCapability.getCapability(capabilityOptions)

      // console.debug('createLayer - configLayerCapability', capability?.titleid, capability)
      if (capability) {
        if (capability.layers) {
          // Store layers inside capability object for future reference
          capabilityOptions.layers = capability.layers
        }
        const olLayer = await this.createNestedLayerGroups(capability)
        return olLayer
      } else {
        console.warn('MapLayerCapabilities createLayer - No capabilities created.', capabilityOptions)
      }
    } catch (error) {
      console.error('MapLayerCapabilities createLayer - Unexpected error', capabilityOptions?.titleid, error)
    }

    return null
  }

  private static async createNestedLayerGroups (capabilityOptions: ConfigLayerCapabilityOptions, layerConf?: ConfigLayerOptions) {
    // console.debug('createNestedLayerGroups - capabilityOptions', capabilityOptions.titleid, layerConf?.titleid, layerConf)

    // If no layerConf is provided, treat the capabilityOptions as a layerConf
    if (!layerConf) {
      layerConf = capabilityOptions
    }

    if (!layerConf) {
      console.log('createNestedLayerGroups - called without layerConf. Doing nothing.')
      return
    }

    // Leaf node
    if (!layerConf.layers) {
      const olChildLayer = await createOlLayerFromLayerConf(layerConf)

      if (!olChildLayer) {
        console.warn('No OL child layer created somehow. Not doing anything', layerConf.titleid, layerConf.type, layerConf)
        return
      }

      // Pozi has a close relationship between layer visibility and whether it is selected or not.
      // The LayerTree component can only turn layers on/off, so here we
      // automatically select/unselect the layer at the same time.
      const toggleSelect = (evt: any) => {
        const { oldValue: layerWasVisible, target: olLayer } = evt

        const titleid = olLayer.get('titleid')
        if (!titleid) {
          console.log('Layer event change:visible - titleid not found:', olLayer)
          return
        }

        console.debug('toggleSelect - layer was visible?', titleid, layerWasVisible)

        const shouldSelectLayer = !layerWasVisible
        if (shouldSelectLayer) {
          console.debug('toggleSelect - dispatch selected', titleid, shouldSelectLayer)

          window.store.dispatch(setLayerSelected(titleid))
        } else {
          console.debug('toggleSelect - dispatch UNselected', titleid, shouldSelectLayer)

          window.store.dispatch(setLayerUnselected(titleid))
        }
      }

      olChildLayer.on('change:visible', toggleSelect)

      // TODO: find a way to do this without needing to store the layerConf in the OL layer
      olChildLayer.set('layerConf', layerConf)

      return olChildLayer
    }

    // Branche node (i.e. nested node)
    const olLayer = new OLLayerGroup({
      opacity: layerConf.opacity ?? 1,
      maxResolution: layerConf.maxResolution,
      minResolution: layerConf.minResolution,
      visible: true // the group layers are always visible, otherwise the child layers won't show!
    })

    // Set some useful properties to the layer
    olLayer.setProperties({
      title: layerConf.title,
      titleid: layerConf.titleid,
      group: layerConf.group,
      groupid: layerConf.groupid
      // initialised: false // custom property to determine if we have initialised this layer
    })

    const layerConfs = [...layerConf.layers]

    // Reverse layer traversing so they show up in the right order in the layer menu
    // Only for WMSGetCapabilities and QGISProjectSettings layers
    // Yes, this is a bit of a hack...
    const layerType = capabilityOptions.type

    const shouldReverse = layerType === ConfigLayerType.QGIS_PROJECT_SETTINGS ||
      layerType === ConfigLayerType.WFS_GET_CAPABILITIES ||
      layerType === ConfigLayerType.WMS_GET_CAPABILITIES ||
      layerType === ConfigLayerType.WMTS_CAPABILITIES

    if (shouldReverse) {
      layerConfs.reverse()
    }

    const childLayers: OLLayer[] = []
    for (const childConf of layerConfs) {
      const childLayer = await this.createNestedLayerGroups(capabilityOptions, childConf)

      // console.debug('createNestedLayerGroups - childLayer', capabilityOptions.titleid, childConf.titleid, childLayer)

      if (!childLayer) {
        console.log('createNestedLayerGroups - no childLayer created. Ignoring', childConf.titleid, childConf.type)
        continue
      }

      // Keep track of parent/child relationships
      childLayer.set('parentLayer', olLayer)

      childLayers.push(childLayer)
    }

    // Add child layers to layer group
    olLayer.setLayers(new OLCollection(childLayers))

    // TODO: find a way to do this without needing to store the layerConf in the OL layer
    olLayer.set('layerConf', layerConf)

    // console.debug('MapLayerCapabilities - createNestedLayerGroups', layerConf.titleid, olLayer, layerConf, childLayers)

    return olLayer
  }
}
